import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Grid from "@mui/material/Unstable_Grid2";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import "./App.css";
import { useInitialData, useOffenseData } from "./hooks";
import { SentenceTimeline, Warning, InfoTabs } from "./components";
import { formatNumber } from "./utils";
import { Offense, PriorCommitments } from "./types";
import { track } from "./analytics";
import { Onboarding } from "./components/Onboarding";
import { MenuItem, Select, SelectChangeEvent } from "@mui/material";

const INITIAL_OFFENSE_CODE = "579.015-001Y202235";
const INITIAL_OFFENSE_DESCRIPTION =
  "POSSESSION OF CONTROLLED SUBSTANCE";

function App() {
  const theme = useTheme();
  const tabletWidth = useMediaQuery(theme.breakpoints.down("lg"));
  const { offenses } = useInitialData();
  const {
    data: offenseData,
    updateData,
    updateSentence,
    updatePriorCommitments,
    isCurrentFelonyDangerous,
  } = useOffenseData();

  const [startOnboarding, setStartOnboarding] = useState(false);

  // State that depends on user input
  const [offense, setOffense] = useState<Offense>({
    code: INITIAL_OFFENSE_CODE,
    description: `${INITIAL_OFFENSE_CODE}: ${INITIAL_OFFENSE_DESCRIPTION}`,
  });
  const [sentenceImposed, setSentenceImposed] = useState<number>(1); // in years
  const [jailTime, setJailTime] = useState<number>(0); // in months
  const [priorCommitments, setPriorCommitments] =
    useState<PriorCommitments>("0"); // in months

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: Offense) => option.description,
  });

  window.Intercom("boot", {
    api_base: "https://api-iam.intercom.io",
    app_id: "b0upuo23",
  });

  // Runs when app loads
  useEffect(() => {
    const returnData = updateData(INITIAL_OFFENSE_CODE);
    if (returnData) {
      setSentenceImposed(returnData.defaultSentence);
    }
  }, [updateData]);

  if (!offenseData) return null;

  return (
    <Box
      sx={{
        p: { lg: "64px 128px 0 48px", md: "48px", sm: "24px", xs: "12px" },
      }}
    >
      <Onboarding
        startOnboarding={startOnboarding}
        setStartOnboarding={setStartOnboarding}
      />
      <Grid container display="flex" flexDirection="column">
        {tabletWidth && (
          <Grid>
            <Typography
              id="app-title"
              variant="h4"
              fontWeight="600"
              sx={{ mb: "16px", width: "fit-content" }}
            >
              Missouri Time Served Calculator{" "}
              <HelpOutlineIcon
                fontSize="small"
                onClick={() => setStartOnboarding(true)}
                sx={{ cursor: "pointer" }}
                color="info"
              />
            </Typography>
            <Typography sx={{ mb: "32px" }}>
              Select a charge and see the range of time to be served based on
              current rules (statutes and regulations)
            </Typography>
          </Grid>
        )}
        <Grid container display="flex" flexDirection="row">
          <Grid xl={3} lg={4} md={6} sm={6} xs={12}>
            <div id="charge-code" className="sentence-name">
              <p className="menu-descriptions"> Charge </p>
              <Autocomplete
                id="combo-box-demo"
                options={offenses}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      style: { borderRadius: "8px", padding: "12px 16px" },
                    }}
                    inputProps={{
                      ...params.inputProps,
                      style: {
                        fontWeight: "bold",
                        fontSize: tabletWidth ? "18px" : "24px",
                        padding: 0,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                )}
                value={offense}
                onChange={(_, value) => {
                  if (value !== null) {
                    setOffense(value);
                    const returnData = updateData(value.code);
                    if (returnData) {
                      setPriorCommitments("0");
                      setSentenceImposed(returnData.defaultSentence);
                      track("update_charge", {
                        charge_code: returnData.chargeCode,
                        sentence_imposed: returnData.defaultSentence,
                        jail_time: jailTime,
                        minimum_percentage: returnData.minPercentage,
                        maximum_percentage: returnData.maxPercentage,
                      });
                    }
                  }
                }}
                getOptionLabel={(option) => option.code}
                renderOption={(props, option) => (
                  <li {...props}>{option.description}</li>
                )}
                isOptionEqualToValue={(option, value) =>
                  option.description === value.description
                }
                filterOptions={filterOptions}
                disablePortal
                fullWidth={true}
              />
            </div>

            <div className="incident-name">{offenseData["Incident Name"]}</div>

            <div className="felony-class-offense-type">
              <div className="felony-class-offense-type-row">
                <span> Felony Class</span>{" "}
                {offenseData["Felony Class Expanded"]}
              </div>
              <div className="felony-class-offense-type-row">
                <span>Offense Type </span>
                {offenseData["Parole Offense Type Expanded"]}
              </div>
            </div>

            <div id="inputs">
              <div>
                <p className="menu-descriptions"> Sentence Imposed (years) </p>
                <div>
                  <TextField
                    id="outlined-number"
                    type="number"
                    value={sentenceImposed}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const newSentence = parseInt(event.target.value);
                      if (newSentence) {
                        setSentenceImposed(newSentence);
                        const { maxPercentage } = updateSentence(
                          newSentence,
                          priorCommitments
                        );
                        track("update_sentence", {
                          charge_code: offense.code,
                          sentence_imposed: newSentence,
                          jail_time: jailTime,
                          minimum_percentage: offenseData["Minimum Percentage"],
                          maximum_percentage: maxPercentage,
                        });
                      }
                    }}
                    InputProps={{
                      style: { borderRadius: "8px", padding: "12px 16px" },
                    }}
                    inputProps={{
                      min: 1,
                      max: formatNumber(offenseData["Sentencing Range"][1]),
                      style: {
                        fontWeight: "bold",
                        fontSize: tabletWidth ? "18px" : "24px",
                        padding: 0,
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth={true}
                  />
                </div>

                <div className="sentencing-range">
                  <span>Sentencing Range </span>
                  {formatNumber(offenseData["Sentencing Range"][0])} -{" "}
                  {formatNumber(offenseData["Sentencing Range"][1])} years
                </div>
              </div>
              <div>
                <Warning
                  enteredSentenceImposed={sentenceImposed}
                  maxSentenceImposed={Number(
                    offenseData["Sentencing Range"][1]
                  )}
                />
              </div>

              <div className="input-wrapper">
                <div className="jail-time-container">
                  <p className="menu-descriptions">
                    Jail Time Credited (months){" "}
                  </p>
                  <div className="jail-time-input">
                    <TextField
                      sx={{ mb: "16px" }}
                      id="outlined-number"
                      value={jailTime}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        const newJailTime = parseInt(event.target.value);
                        if (!isNaN(newJailTime)) {
                          setJailTime(newJailTime);
                          track("update_jail_time", {
                            charge_code: offense.code,
                            sentence_imposed: sentenceImposed,
                            jail_time: newJailTime,
                            minimum_percentage:
                              offenseData["Minimum Percentage"],
                            maximum_percentage:
                              offenseData["Maximum Percentage"],
                          });
                        }
                      }}
                      type="number"
                      InputProps={{
                        style: { borderRadius: "8px", padding: "12px 16px" },
                      }}
                      inputProps={{
                        min: 0,
                        style: {
                          fontWeight: "bold",
                          fontSize: tabletWidth ? "18px" : "24px",
                          padding: 0,
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth={true}
                    />
                    {offenseData["Minimum Percentage"] !== undefined &&
                    jailTime >
                      sentenceImposed *
                        offenseData["Minimum Percentage"] *
                        12 ? (
                      <p
                        style={{
                          color: "black",
                          textAlign: "left",
                        }}
                      >
                        Individual will be eligible for parole upon intake to
                        Department of Corrections.
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              {offenseData.isMPT && !isCurrentFelonyDangerous && (
                <div className="input-wrapper">
                  <div>
                    <p className="menu-descriptions">
                      Number of prior felony commitments for offenses listed in{" "}
                      <a
                        href="https://revisor.mo.gov/main/OneSection.aspx?section=558.019&bid=47815&hl"
                        target="_blank"
                        rel="noreferrer"
                      >
                        RSMo § 558.019
                      </a>
                    </p>
                    <div>
                      <Select
                        sx={{ mb: "16px", borderRadius: "8px" }}
                        value={priorCommitments}
                        onChange={(event: SelectChangeEvent) => {
                          setPriorCommitments(
                            event.target.value as PriorCommitments
                          );
                          updatePriorCommitments(
                            sentenceImposed,
                            event.target.value as PriorCommitments,
                            offenseData["Parole Offense Type"],
                            offenseData["Felony Class"],
                            offenseData?.isMPT
                          );
                        }}
                        fullWidth
                        SelectDisplayProps={{
                          style: {
                            borderRadius: "8px",
                            padding: "12px 16px",
                            fontWeight: "bold",
                            fontSize: tabletWidth ? "18px" : "24px",
                            display: "flex",
                            alignItems: "center",
                          },
                        }}
                      >
                        <MenuItem value="0">0</MenuItem>
                        <MenuItem value="1">1</MenuItem>
                        <MenuItem value="2">2</MenuItem>
                        <MenuItem value="3+">3+</MenuItem>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="m-link">
              <a href="https://docs.google.com/document/d/1zsTRB7REAweOH-2I4x7m6-3PNOsC7bKsSnpBGcHyoOo/edit">
                Methodology
              </a>
            </div>
          </Grid>
          <Grid
            xl={8}
            xlOffset={1}
            lg={7}
            lgOffset={1}
            md={5}
            mdOffset={1}
            sm={5}
            smOffset={1}
            xs={12}
            container
            display="flex"
            flexDirection="column"
          >
            {!tabletWidth && (
              <Grid>
                <Typography
                  id="app-title"
                  variant="h4"
                  fontWeight="600"
                  sx={{ mb: "16px", width: "fit-content" }}
                >
                  Missouri Time Served Calculator{" "}
                  <HelpOutlineIcon
                    fontSize="small"
                    onClick={() => setStartOnboarding(true)}
                    sx={{ cursor: "pointer" }}
                    color="info"
                  />
                </Typography>
                <Typography sx={{ mb: "24px" }}>
                  Select a charge and see the range of time to be served based
                  on current rules (statutes and regulations)
                </Typography>
              </Grid>
            )}

            <Grid container display="flex" flexDirection="row">
              <Grid id="timeline" lg={6} sm={12}>
                <h5> Range of Time to be Served </h5>
                <SentenceTimeline
                  totalSentence={sentenceImposed * 12}
                  minPercent={Number(
                    (offenseData["Minimum Percentage"] * 100).toFixed(2)
                  )}
                  maxPercent={Number(
                    (offenseData["Maximum Percentage"] * 100).toFixed(2)
                  )}
                  jailTimeMonths={jailTime}
                />
              </Grid>

              {!tabletWidth && (
                <Grid id="rules" lg={5} lgOffset={1}>
                  <h5>Relevant Rules</h5>
                  <InfoTabs offenseData={offenseData} isCurrentFelonyDangerous={isCurrentFelonyDangerous}/>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        {tabletWidth && (
          <Grid id="rules">
            <h5>Relevant Rules</h5>
            <InfoTabs offenseData={offenseData} isCurrentFelonyDangerous={isCurrentFelonyDangerous}/>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default App;
